<template>
  <AppLayout>
    <div class="col-12 layout-spacing">
      <div class="widget widget-six">
        <div class="widget-heading">
          <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <h4 class="my-0">Planowanie urlopów na rok</h4>

            <div class="d-flex align-items-center gap-2">
              <select
                id="yearSelect"
                v-model.number="selectedYearValue"
                class="form-select year-select"
                @change="changeYear"
              >
                <option
                  v-for="year in availableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="widget-content">
          <!-- Komunikat -->
          <div
            v-if="notification.message"
            class="alert"
            :class="notification.type === 'success' ? 'alert-success' : 'alert-danger'"
            role="alert"
          >
            {{ notification.message }}
          </div>

          <div class="row align-items-center mb-3 g-2">
            <div class="col-md-4">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="bi bi-search"></i>
                </span>

                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Szukaj pracownika..."
                  class="form-control"
                />
              </div>
            </div>

            <div class="col-md-8 text-md-end">
              <span class="text-muted">
                Wyświetlono:
                <strong>{{ filteredAndSortedUsers.length }}</strong>
                pracowników
              </span>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered table-hover vacation-table">
              <thead>
                <tr>
                  <th
                    class="text-start employee-column"
                    @click="sortBy('userName')"
                  >
                    Pracownik
                    <span>{{ sortIcon('userName') }}</span>
                  </th>
                  <th>
                    Urlop na {{ rok }}
                  </th>
                  <th>
				    Przeniesione z {{ rok - 1 }}
				  </th>
                  <th>
                    Razem na {{ rok }}
                  </th>

                  <th>
                    Wykorzystane
                  </th>

                  <th class="remaining-column">
                    Pozostało z {{ rok }}
                  </th>

                  <th class="next-year-column">
                    Urlop {{ nextYear }}
                  </th>

                  <th class="total-column">
                    Razem {{ nextYear }}
                  </th>

                  <th class="save-column">
                    Zapisz
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="user in filteredAndSortedUsers"
                  :key="user.user_id"
                >
                  <td class="text-start fw-semibold">
                    {{ user.userName || 'Pracownik #' + user.user_id }}
                  </td>

                  <td>{{ user.ten_rok }}</td>
                  <td>{{ user.poprzedni_rok }}</td>
                  <td class="fw-semibold">
                    {{ user.razem_wolne }}
                  </td>

                  <td>
                    <span
                      class="days-badge used"
                      :class="{ 'zero-value': !user.wykorzystaneDni }"
                    >
                      {{ user.wykorzystaneDni }}
                    </span>
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      max="366"
                      v-model.number="user.zostalo_wolne"
                      class="form-control days-input"
                    />
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      max="366"
                      v-model.number="user.dniZaStaz"
                      class="form-control days-input"
                    />
                  </td>

                  <td class="fw-bold total-cell">
                    <span class="days-badge total">
                      {{ totalForNextYear(user) }}
                    </span>
                  </td>

                  <td class="text-center">
                    <button
                      type="button"
                      class="btn btn-success btn-sm save-row-button"
                      :disabled="savingUserId !== null"
                      @click="saveUser(user)"
                    >
                      <span
                        v-if="savingUserId === user.user_id"
                        class="spinner-border spinner-border-sm"
                      ></span>

                      <template v-else>
                        <i class="bi bi-save me-1"></i>
                        Zapisz
                      </template>
                    </button>
                  </td>
                </tr>

                <tr v-if="filteredAndSortedUsers.length === 0">
                  <td colspan="9" class="text-center py-5 text-muted">
                    <i class="bi bi-inbox fs-2 d-block mb-2"></i>
                    Brak pracowników do wyświetlenia
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from "@/Layouts/App.vue";
import axios from "axios";
import { router } from "@inertiajs/vue3";

export default {
  components: {
    AppLayout,
  },

  props: {
    users: {
      type: Array,
      default: () => [],
    },

    rok: {
      type: Number,
      required: true,
    },

    nextYear: {
      type: Number,
      required: true,
    },

    selectedYear: {
      type: Number,
      required: true,
    },

    availableYears: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      searchQuery: "",
      sortKey: "userName",
      sortOrder: "asc",
      savingUserId: null,
      selectedYearValue: this.selectedYear,

      notification: {
        message: "",
        type: "success",
      },
    };
  },

  computed: {
    filteredAndSortedUsers() {
      let result = [...this.users];

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();

        result = result.filter((user) =>
          String(user.userName || `Pracownik ${user.user_id}`)
            .toLowerCase()
            .includes(query)
        );
      }

      result.sort((a, b) => {
        let aValue = a[this.sortKey] ?? 0;
        let bValue = b[this.sortKey] ?? 0;

        if (this.sortKey === "userName") {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        } else {
          aValue = Number(aValue);
          bValue = Number(bValue);
        }

        if (aValue < bValue) {
          return this.sortOrder === "asc" ? -1 : 1;
        }

        if (aValue > bValue) {
          return this.sortOrder === "asc" ? 1 : -1;
        }

        return 0;
      });

      return result;
    },
  },

  methods: {
    changeYear() {
      router.get(
        route("kadry.urlopy.nowy-rok"),
        {
          rok: this.selectedYearValue,
        },
        {
          preserveState: false,
          preserveScroll: true,
        }
      );
    },

    totalForNextYear(user) {
      return (
        Number(user.zostalo_wolne || 0) +
        Number(user.dniZaStaz || 0)
      );
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder =
          this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "desc";
      }
    },

    sortIcon(key) {
      if (this.sortKey !== key) {
        return "↕";
      }

      return this.sortOrder === "asc" ? "▲" : "▼";
    },

async saveUser(user) {
  if (this.savingUserId !== null) {
    return;
  }

  this.savingUserId = user.user_id;

  try {
    const response = await axios.post(
      route("kadry.urlopy.nowy-rok.zapisz"),
      {
        rok: this.nextYear,
        user_id: user.user_id,
        zostalo_wolne: Number(user.zostalo_wolne || 0),
        dniZaStaz: Number(user.dniZaStaz || 0),
      },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data && response.data.success === true) {
      this.showToast(
        "success",
        "Zapisano",
        response.data.message || "Dane urlopowe zostały zapisane."
      );
    } else {
      this.showToast(
        "error",
        "Błąd",
        response.data?.message || "Serwer nie potwierdził zapisu."
      );
    }
  } catch (error) {
    console.error("Błąd zapisu urlopu:", error);

    let message = "Nie udało się zapisać danych.";

    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }

    this.showToast("error", "Błąd", message);
  } finally {
    this.savingUserId = null;
  }
},
	    showToast(icon, title, message) {
      if (typeof Toast === 'undefined') {
        alert(`${title}: ${message}`);
        return;
      }

      Toast.fire({
        position: 'top-end',
        toast: true,
        icon,
        title,
        html: message,

        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },

        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },

        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true
      });
    },
  },
};
</script>

<style scoped>
.year-select {
  width: 110px;
}

.vacation-table {
  white-space: nowrap;
  vertical-align: middle;
  margin-bottom: 0;
}

.vacation-table th {
  background: #f5f6fa;
  color: #333;
  font-size: 12px;
  font-weight: 700;
  padding: 13px 10px;
  cursor: pointer;
  vertical-align: middle;
}

.vacation-table td {
  padding: 11px 10px;
  font-size: 13px;
  vertical-align: middle;
  height: 43px;
}

.vacation-table th span {
  color: #999;
  margin-left: 3px;
  font-size: 10px;
}

.employee-column {
  min-width: 190px;
}

.remaining-column {
  background: #e8f5ed !important;
}

.next-year-column {
  background: #eaf2ff !important;
}

.total-column {
  background: #e5f5eb !important;
}

.save-column {
  min-width: 95px;
  cursor: default !important;
}

.days-badge {
  display: inline-block;
  min-width: 35px;
  padding: 5px 10px;
  border-radius: 6px;
  font-weight: 700;
}

.days-badge.used {
  background: #fff0e4;
  color: #d97706;
}

.days-badge.zero-value {
  background: #f1f2f4;
  color: #999;
}

.days-badge.total {
  background: #dff3e7;
  color: #126c3a;
  font-size: 14px;
}

.total-cell {
  background: #f6fbf7;
}

.days-input {
  width: 85px;
  min-width: 75px;
  text-align: center;
  font-weight: 700;
  padding: 5px;
}

.save-row-button {
  min-width: 75px;
}
</style>